import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { getRecaptchaToken } from "@/lib/recaptcha";

export default function Comments({ articleId }) {
  const [comments, setComments] = useState([]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [content, setContent] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/comments/${articleId}`);
      setComments(data);
    } catch { /* ignore */ }
  };

  useEffect(() => { load(); }, [articleId]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const recaptcha_token = await getRecaptchaToken("comment");
      await api.post("/comments", { article_id: articleId, name, email: email || undefined, content, recaptcha_token });
      toast.success("Comment posted");
      setName(""); setEmail(""); setContent("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="max-w-3xl mx-auto py-12" data-testid="comments-section">
      <h3 className="font-serif text-3xl font-bold mb-6">Discussion <span className="text-[color:var(--text-muted)] text-lg">({comments.length})</span></h3>

      <form onSubmit={submit} className="space-y-4 mb-10 border p-6" style={{ borderColor: "hsl(var(--border-hsl))" }} data-testid="comment-form">
        <div className="grid md:grid-cols-2 gap-4">
          <input required value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="bg-transparent border px-3 py-2 outline-none" style={{ borderColor: "hsl(var(--border-hsl))" }} data-testid="comment-name" />
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email (optional, not shown)" className="bg-transparent border px-3 py-2 outline-none" style={{ borderColor: "hsl(var(--border-hsl))" }} data-testid="comment-email" />
        </div>
        <textarea required value={content} onChange={(e) => setContent(e.target.value)} rows={4} placeholder="Share your thoughts..." className="w-full bg-transparent border px-3 py-2 outline-none" style={{ borderColor: "hsl(var(--border-hsl))" }} data-testid="comment-content" />
        <button disabled={busy} className="btn-accent" data-testid="comment-submit">{busy ? "Posting..." : "Post comment"}</button>
      </form>

      <div className="space-y-6">
        {comments.length === 0 && <p className="text-[color:var(--text-muted)]">No comments yet. Be the first to weigh in.</p>}
        {comments.map((c) => (
          <div key={c.id} className="border-l-2 pl-4" style={{ borderColor: "var(--accent)" }} data-testid={`comment-${c.id}`}>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 rounded-full bg-[color:var(--text)] text-[color:var(--bg)] flex items-center justify-center font-bold text-sm">
                {c.name.charAt(0).toUpperCase()}
              </div>
              <div>
                <div className="font-semibold">{c.name}</div>
                <div className="text-xs text-[color:var(--text-muted)]">{new Date(c.created_at).toLocaleDateString()}</div>
              </div>
            </div>
            <p className="leading-relaxed">{c.content}</p>
          </div>
        ))}
      </div>
    </section>
  );
}
